import React, { useEffect, useRef, useState } from 'react';
import StudyPage, { StudyCard } from './StudyPage';
import { CONTACT_LINE, DEBRIEF_CALIBRATION, DEBRIEF_MAIN } from '../../data/studyText';

/**
 * Debrief page (advisor §7.3), shown after /complete has validated the record and the
 * credit link is in hand, so a student who closes the tab here keeps their credit.
 * `onShown` fires once when the page renders (debrief.shownAt); `onAcknowledge` when the
 * student ticks the box and continues (debrief.acknowledgedAt).
 */
export interface DebriefPageProps {
  arm: 'calibration' | 'main';
  iface?: string;
  onShown: () => void;
  onAcknowledge: () => Promise<void> | void;
}

const DebriefPage: React.FC<DebriefPageProps> = ({ arm, iface, onShown, onAcknowledge }) => {
  const [read, setRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const shown = useRef(false);
  useEffect(() => { if (!shown.current) { shown.current = true; onShown(); } }, [onShown]);
  const submit = async () => { if (!read || busy) return; setBusy(true); try { await onAcknowledge(); } finally { setBusy(false); } };
  const ackBox = (
    <label className="study-check">
      <input type="checkbox" checked={read} onChange={e => setRead(e.target.checked)} data-testid="debrief-ack" />
      <span>{DEBRIEF_MAIN.ack}</span>
    </label>
  );
  if (arm === 'calibration') {
    return (
      <StudyPage title="Thank you" progress="Last step" formTestId="debrief-form" submitTestId="debrief-continue" primaryLabel="Continue to credit" canSubmit={read && !busy} onSubmit={submit}>
        <StudyCard className="study-debrief">
          <p>{DEBRIEF_CALIBRATION} Questions: {CONTACT_LINE}.</p>
          {ackBox}
        </StudyCard>
      </StudyPage>
    );
  }
  const p3 = DEBRIEF_MAIN.p3[iface || ''] || DEBRIEF_MAIN.p3.ordinary;
  return (
    <StudyPage title={DEBRIEF_MAIN.title} progress="Last step" formTestId="debrief-form" submitTestId="debrief-continue" primaryLabel="Continue to credit" canSubmit={read && !busy} onSubmit={submit}>
      <StudyCard className="study-debrief">
        <p>{DEBRIEF_MAIN.p1}</p>
        <p>{DEBRIEF_MAIN.p2}</p>
        <p data-testid="debrief-arm-paragraph" data-interface={iface}>{p3}</p>
        <h3>{DEBRIEF_MAIN.realTitle}</h3>
        <p>{DEBRIEF_MAIN.real}</p>
        <p>{DEBRIEF_MAIN.closing} Questions: {CONTACT_LINE}.</p>
        {ackBox}
      </StudyCard>
    </StudyPage>
  );
};

export default DebriefPage;
